// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * What the Satellites layer says, the same in the browser and in the
 * headset or phone: legend names, event kinds, and a satellite's card.
 */
import { t, type MessageKey } from '../../i18n'
import { formatDate, formatNumber } from '../../i18n/format'
import { dataService } from '../dataService'
import { displayTitle } from '../datasetTaxonomy'
import { bodyOfDataset } from '../offEarthBody'

/** The app's id of a stream the index names by its stable id (dataService: `R2_DASH_<id>`). */
export function appDatasetId(indexId: string): string {
  return `R2_DASH_${indexId}`
}

/** The index's stable id of an app dataset, or null for one that is not a stream of the index. */
export function indexDatasetId(appId: string): string | null {
  return appId.startsWith('R2_DASH_') ? appId.slice('R2_DASH_'.length) : null
}
import type { ColourBy, SatelliteLayer } from './satelliteLayer'
import { MU_KM3_S2 } from './satelliteOrbit'
import type { SatelliteEvent, SatelliteInfo } from './satelliteTypes'

/** 'human-spaceflight' → 'humanSpaceflight' (locale keys take no hyphens). */
export function camel(key: string): string {
  return key.replace(/-([a-z])/g, (_, c: string) => c.toUpperCase())
}

/** The name of a legend entry under a colouring. */
export function categoryName(by: ColourBy, key: string): string {
  if (key === 'debris') return t('satellites.category.debris')
  if (by === 'orbit') return t(`satellites.orbit.${key}` as MessageKey)
  if (by === 'country') return t(`satellites.country.${key}` as MessageKey)
  return t(`satellites.purpose.${camel(key)}` as MessageKey)
}

export function eventKindName(kind: SatelliteEvent['kind']): string {
  return t(`satellites.events.kind.${camel(kind)}` as MessageKey)
}

/** An event's text in the page's language, English when it has none. */
export function localised(text: { en: string } & Partial<Record<string, string>>, lang: string): string {
  return text[lang] || text.en
}

/** Orbital speed (km/h) at distance r (km) from Earth's centre, by vis-viva. */
export function orbitalSpeedKmh(info: SatelliteInfo, rKm: number): number {
  return Math.sqrt(MU_KM3_S2 * (2 / rKm - 1 / info.semiMajorKm)) * 3600
}

export interface SatelliteFacts {
  name: string
  rows: Array<[string, string]>
}

/** What a satellite's card says. */
export function satelliteFacts(layer: SatelliteLayer, index: number): SatelliteFacts | null {
  const info = layer.engine.infos[index]
  if (!info) return null
  const p = layer.engine.current
  const x = p[index * 3]
  const rows: Array<[string, string]> = []
  rows.push([t('satellites.card.ids'), `NORAD ${info.norad} · ${info.cosparId}`])
  if (x === x) {
    const r = Math.hypot(x, p[index * 3 + 1], p[index * 3 + 2])
    rows.push([t('satellites.card.height'), t('satellites.unit.km', { value: formatNumber(Math.round(r - 6371)) })])
    rows.push([
      t('satellites.card.speed'),
      t('satellites.unit.kmh', { value: formatNumber(Math.round(orbitalSpeedKmh(info, r) / 100) * 100) }),
    ])
  }
  rows.push([t('satellites.card.orbit'), categoryName('orbit', info.orbit)])
  rows.push([
    t('satellites.card.orbitRange'),
    t('satellites.unit.kmRange', {
      low: formatNumber(Math.round(info.perigeeKm)),
      high: formatNumber(Math.round(info.apogeeKm)),
    }),
  ])
  rows.push([
    t('satellites.card.period'),
    t('satellites.unit.min', { value: formatNumber(info.periodMin, { maximumFractionDigits: 1 }) }),
  ])
  rows.push([
    t('satellites.card.inclination'),
    t('satellites.unit.deg', { value: formatNumber(info.inclinationDeg, { maximumFractionDigits: 1 }) }),
  ])
  if (info.owner) {
    rows.push([t('satellites.card.owner'), info.country === 'other' ? info.owner : categoryName('country', info.country)])
  }
  if (info.purpose && info.purpose !== 'other') {
    rows.push([t('satellites.card.purpose'), t(`satellites.purpose.${camel(info.purpose)}` as MessageKey)])
  }
  if (info.launch) {
    rows.push([
      t('satellites.card.launched'),
      formatDate(info.launch, { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' }),
    ])
  }
  rows.push([
    t('satellites.card.elements'),
    formatDate(`${info.epoch}Z`, {
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
      timeZone: 'UTC',
      timeZoneName: 'short',
    }),
  ])
  return { name: info.name, rows }
}

/** One dataset a satellite feeds, as its card lists it (owner, 2026-10-24). */
export interface SatelliteDatasetRow {
  id: string
  title: string
  /** The window of the satellite's entry, as text; null when it is the whole dataset. */
  window: string | null
  /** A `fallback` entry only: used when the main satellite is missing. */
  standIn: boolean
  /** The body the dataset shows (offEarthBody.ts), null for the Earth. */
  body: string | null
  /** In this app's catalogue (loadable). */
  known: boolean
}

function windowText(from: string | undefined, to: string | undefined): string | null {
  const f = (d: string) => formatDate(d, { year: 'numeric', month: 'short', timeZone: 'UTC' })
  if (from && to) return t('satellites.card.window', { from: f(from), to: f(to) })
  if (from) return t('satellites.card.windowFrom', { from: f(from) })
  if (to) return t('satellites.card.windowTo', { to: f(to) })
  return null
}

/**
 * The datasets a satellite feeds (`sources.json`), one row per dataset:
 * several eras of one satellite (GOES-7 on GOES generations) fold into one
 * row whose windows are listed. Empty without the index.
 */
export function satelliteDatasets(layer: SatelliteLayer, norad: number): SatelliteDatasetRow[] {
  const rows = new Map<string, SatelliteDatasetRow>()
  for (const e of layer.datasetsOf(norad)) {
    if (!e || typeof e.id !== 'string') continue
    const ds = dataService.getDatasetById(appDatasetId(e.id))
    const window = windowText(e.from, e.to)
    const standIn = e.role === 'fallback'
    const prev = rows.get(e.id)
    if (prev) {
      if (window && prev.window && !prev.window.includes(window)) prev.window = `${prev.window}, ${window}`
      else if (window && !prev.window) prev.window = window
      prev.standIn = prev.standIn && standIn
      continue
    }
    rows.set(e.id, {
      id: appDatasetId(e.id),
      title: ds ? displayTitle(ds) : e.displayName || e.name,
      window,
      standIn,
      body: ds ? bodyOfDataset(ds) : null,
      known: !!ds,
    })
  }
  return [...rows.values()]
}

/** For a satellite the mirror does not carry: "Retired 2018; not drawn" from the index, else null. */
export function platformNote(layer: SatelliteLayer, norad: number): string | null {
  const p = layer.platformOf(norad)
  if (!p || p.tracked) return null
  let year = p.decay?.slice(0, 4) ?? null
  if (!year) {
    const ends = p.datasets.map((d) => d.to).filter((v): v is string => typeof v === 'string').sort()
    const last = ends[ends.length - 1]
    // A window that closed in the past says when it stopped; one still open says nothing.
    if (last && Date.parse(last) < Date.now() - 365 * 86_400_000 && !p.datasets.some((d) => !d.to)) year = last.slice(0, 4)
  }
  return year ? t('satellites.card.retired', { year }) : t('satellites.card.notTracked')
}

/** The card as lines of text, for the panels beside the headset/phone globe. */
export function satelliteFactsText(layer: SatelliteLayer, index: number): string | null {
  const facts = satelliteFacts(layer, index)
  if (!facts) return null
  const lines = [facts.name, ...facts.rows.map(([k, v]) => `${k}: ${v}`)]
  const info = layer.engine.infos[index]
  const data = info ? satelliteDatasets(layer, info.norad) : []
  if (data.length) {
    lines.push(`${t('satellites.card.datasets')}:`)
    for (const r of data) lines.push(`• ${r.title}${r.window ? ` (${r.window})` : ''}${r.standIn ? ` – ${t('satellites.card.standIn')}` : ''}`)
    if (data.some((r) => r.body === 'sun')) lines.push(t('satellites.card.showsSun'))
  }
  lines.push(t('satellites.approximate'))
  return lines.join('\n')
}

/** Opens a space event: what it is about, the object's orbit, or the card of one that is gone. */
export function showEvent(layer: SatelliteLayer, e: SatelliteEvent): void {
  layer.focusEvent(e)
}

/** "12.3° N" / "45.6° W": a coordinate for a card. */
export function coordText(value: number, axis: 'lat' | 'lon'): string {
  const hemi = axis === 'lat' ? (value < 0 ? 's' : 'n') : value < 0 ? 'w' : 'e'
  return `${formatNumber(Math.abs(value), { maximumFractionDigits: 1 })}° ${t(`satellites.hemi.${hemi}` as MessageKey)}`
}

export interface EventFacts {
  kind: SatelliteEvent['kind']
  title: string
  date: string
  text: string | null
  rows: Array<[string, string]>
  /** What the line on the globe is, and how far to trust the place. */
  note: string | null
  source: string | null
  /** A place to show on the globe, degrees. */
  place: { lat: number; lon: number } | null
  /** The datasets the event's satellites feed (satellites ↔ catalog plan, step 2), with Load. */
  datasets: SatelliteDatasetRow[]
}

/**
 * What a space event's card says (owner, 2026-10-24): the event itself, and
 * for an object that has left the data its last orbit and where it came down.
 * The same in the browser, the Tools card and the headset or phone.
 */
export function eventFacts(layer: SatelliteLayer, lang: string): EventFacts | null {
  const focus = layer.eventFocus
  if (!focus) return null
  const e = focus.event
  const rows: Array<[string, string]> = []
  let note: string | null = null
  let place: EventFacts['place'] = null
  const view = focus.view
  if (focus.loading) note = t('satellites.event.working')
  else if (view) {
    const s = view.shape
    rows.push([
      t('satellites.card.orbitRange'),
      t('satellites.unit.kmRange', { low: formatNumber(Math.round(s.perigeeKm)), high: formatNumber(Math.round(s.apogeeKm)) }),
    ])
    rows.push([t('satellites.card.period'), t('satellites.unit.min', { value: formatNumber(s.periodMin, { maximumFractionDigits: 1 }) })])
    rows.push([t('satellites.card.inclination'), t('satellites.unit.deg', { value: formatNumber(s.inclinationDeg, { maximumFractionDigits: 1 }) })])
    rows.push([
      t('satellites.card.elements'),
      formatDate(view.epochMs, { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit', timeZone: 'UTC', timeZoneName: 'short' }),
    ])
    if (view.place) {
      const args = { lat: coordText(view.place.lat, 'lat'), lon: coordText(view.place.lon, 'lon') }
      rows.push([t('satellites.event.came'), t(view.place.approximate ? 'satellites.event.placeAbout' : 'satellites.event.place', args)])
      place = { lat: view.place.lat, lon: view.place.lon }
      note = t(view.place.from === 'estimated' ? 'satellites.event.noteEstimated' : 'satellites.event.noteGiven')
    } else {
      if (e.kind === 'reentry') rows.push([t('satellites.event.came'), t('satellites.event.noPlace')])
      note = t('satellites.event.noteGiven')
    }
  } else if (e.kind === 'reentry' || e.past) note = t('satellites.event.noOrbit')
  const place0 = e.past?.place
  if (!place && place0 && Number.isFinite(place0.lat) && Number.isFinite(place0.lon)) place = { lat: place0.lat, lon: place0.lon }
  const datasets: SatelliteDatasetRow[] = []
  const seen = new Set<string>()
  for (const n of e.norad ?? []) for (const r of satelliteDatasets(layer, n)) if (!seen.has(r.id)) { seen.add(r.id); datasets.push(r) }
  return {
    kind: e.kind,
    title: localised(e.title, lang),
    date: formatDate(e.date, { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' }),
    text: e.text ? localised(e.text, lang) : null,
    rows,
    note,
    source: e.source && /^https:\/\//.test(e.source) ? e.source : null,
    place,
    datasets,
  }
}
